import React, { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { searchAPI } from "../lib/api";
import { Input } from "./ui/input";
import {
  Search,
  FileText,
  Users,
  History,
  ArrowRight,
  Loader2,
  X,
} from "lucide-react";

export function GlobalSearch({ collapsed }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);
  const navigate = useNavigate();

  const doSearch = useCallback(async (q) => {
    if (q.length < 2) {
      setResults(null);
      setOpen(false);
      return;
    }
    setLoading(true);
    try {
      const res = await searchAPI.search(q);
      setResults(res.data);
      setOpen(true);
    } catch {
      setResults(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleChange = (e) => {
    const val = e.target.value;
    setQuery(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => doSearch(val), 300);
  };

  const handleNavigate = (path) => {
    setOpen(false);
    setQuery("");
    setResults(null);
    navigate(path);
  };

  const handleClear = () => {
    setQuery("");
    setResults(null);
    setOpen(false);
    inputRef.current?.focus();
  };

  // Close on click outside
  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Keyboard shortcut: Ctrl+K
  useEffect(() => {
    const handler = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "k") {
        e.preventDefault();
        inputRef.current?.focus();
      }
      if (e.key === "Escape") {
        setOpen(false);
        inputRef.current?.blur();
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, []);

  if (collapsed) {
    return (
      <button
        onClick={() => inputRef.current?.focus()}
        className="w-full flex justify-center p-2 rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
        title="Cerca (Ctrl+K)"
        data-testid="global-search-collapsed"
      >
        <Search size={20} />
      </button>
    );
  }

  const hasResults = results && results.total > 0;
  const noResults = results && results.total === 0;

  return (
    <div ref={containerRef} className="relative w-full" data-testid="global-search">
      <div className="relative">
        <Search
          size={16}
          className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none"
        />
        <Input
          ref={inputRef}
          value={query}
          onChange={handleChange}
          onFocus={() => results && setOpen(true)}
          placeholder="Cerca... (Ctrl+K)"
          className="pl-9 pr-8 h-9 bg-muted/50 border-transparent focus:border-input focus:bg-background text-sm"
          data-testid="global-search-input"
        />
        {query && (
          <button
            onClick={handleClear}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
          >
            {loading ? <Loader2 size={14} className="animate-spin" /> : <X size={14} />}
          </button>
        )}
      </div>

      {/* Results Dropdown */}
      {open && (hasResults || noResults) && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-card border border-border rounded-lg shadow-xl z-[60] max-h-[400px] overflow-y-auto">
          {noResults && (
            <div className="p-4 text-sm text-muted-foreground text-center" data-testid="search-no-results">
              Nessun risultato per "{query}"
            </div>
          )}

          {/* Protocols */}
          {results?.protocols?.length > 0 && (
            <div>
              <div className="px-3 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider bg-muted/30 border-b border-border flex items-center gap-1.5">
                <FileText size={12} />
                Protocolli
              </div>
              {results.protocols.map((p) => (
                <button
                  key={p.id}
                  onClick={() => handleNavigate(`/protocols/${p.id}`)}
                  className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-muted transition-colors text-left group"
                  data-testid={`search-result-protocol-${p.id}`}
                >
                  <div className={`w-2 h-2 rounded-full flex-shrink-0 ${
                    p.direction === "entrata" ? "bg-emerald-500" : "bg-blue-500"
                  }`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-foreground">
                        {p.protocol_number}
                      </span>
                      {p.contact_name && (
                        <span className="text-xs text-muted-foreground truncate">
                          - {p.contact_name}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground truncate">
                      {p.subject}
                    </p>
                  </div>
                  <ArrowRight size={14} className="text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
                </button>
              ))}
            </div>
          )}

          {/* Contacts */}
          {results?.contacts?.length > 0 && (
            <div>
              <div className="px-3 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider bg-muted/30 border-b border-border flex items-center gap-1.5">
                <Users size={12} />
                Contatti
              </div>
              {results.contacts.map((c) => (
                <button
                  key={c.id}
                  onClick={() => handleNavigate(`/settings/contacts/${c.id}/protocols`)}
                  className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-muted transition-colors text-left group"
                  data-testid={`search-result-contact-${c.id}`}
                >
                  <div className="w-7 h-7 rounded-full bg-[#2D7BB7]/10 text-[#2D7BB7] flex items-center justify-center flex-shrink-0">
                    <Users size={14} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">{c.name}</p>
                    <p className="text-xs text-muted-foreground capitalize">{c.category}{c.email ? ` - ${c.email}` : ""}</p>
                  </div>
                  <ArrowRight size={14} className="text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
                </button>
              ))}
            </div>
          )}

          {/* Audit Logs */}
          {results?.audit_logs?.length > 0 && (
            <div>
              <div className="px-3 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider bg-muted/30 border-b border-border flex items-center gap-1.5">
                <History size={12} />
                Audit Log
              </div>
              {results.audit_logs.map((log) => (
                <button
                  key={log.id}
                  onClick={() => handleNavigate("/settings/audit")}
                  className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-muted transition-colors text-left group"
                  data-testid={`search-result-audit-${log.id}`}
                >
                  <div className="w-7 h-7 rounded-full bg-amber-500/10 text-amber-600 flex items-center justify-center flex-shrink-0">
                    <History size={14} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">
                      {log.entity_name}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {log.action} da {log.user_name}
                    </p>
                  </div>
                  <ArrowRight size={14} className="text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
